import React, { useState } from "react";
import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Paper as MuiPaper,
  CardContent,
  Box,
  Chip,
  Button,
} from "@mui/material";
import { red, yellow } from "@mui/material/colors";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";

import EditDelete from "../../../../components/EditDelete";
import Toolbar from "../../../../components/Toolbar";
import { useDialog } from "../../../../contexts/DialogContext";
import { getTimeStamp } from "../../../../utils/common";
import useWindowDimensions from "../../../../utils/windowDimensions";
import AdvanceFilter from "../../userlist/components/AdvanceFilter";
import useUser from "../../../../hooks/useUser";
import useMainContentDimensions from "../../../../utils/mainContentDimensions";
import useAuth from "../../../../hooks/useAuth";
import DeleteUsersModal from "./DeleteUsersModal";

// Roles allowed to delete users (same as the API)
const DELETE_ROLES = [
  "SUPER_ADMIN",
  "ADMIN",
  "MULTI_SERVICE_OPERATOR",
  "DISTRIBUTOR",
  "LOCAL_CABLE_OPERATOR",
  "BULK_BANDWIDTH",
  "HOTEL",
];

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const breadCrump = () => {
  return (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>Masters</Typography>
      <Typography>Suspened Users</Typography>
      <Typography>List</Typography>
    </Breadcrumbs>
  );
};

const userStatus = (status) => {
  const chipStyles = {
    width: "100px", // Set the desired width for all chips
    textAlign: "center", // Center align the text in each chip
  };

  if (status === "active") {
    return <Chip label="Active" color="primary" sx={chipStyles} />;
  } else if (status === "online") {
    return <Chip label="Online" color="success" sx={chipStyles} />;
  } else if (status === "expired") {
    return <Chip label="Expired" color="warning" sx={chipStyles} />;
  } else if (status === "offline") {
    return (
      <Chip label="Offline" sx={{ ...chipStyles, backgroundColor: "grey.500", color: "white" }} />
    );
  } else if (status === "suspended") {
    return (
      <Chip label="Suspended" sx={{ ...chipStyles, backgroundColor: red[700], color: "white" }} />
    );
  } else if (status === "on_hold") {
    return (
      <Chip label="On Hold" sx={{ ...chipStyles, backgroundColor: yellow[700], color: "white" }} />
    );
  }
};

const CustomDataGrid = (props) => {
  const { isLoadingDownload, downloadFileType } = useUser();

  const { height } = useWindowDimensions();
  const { width: mainContentWidth } = useMainContentDimensions();

  const {
    editPage,
    addPage,
    trans,
    rows,
    rowCount,
    payload,
    setPayload,
    isLoading,
    id,
    handleSortModelChange,
    sortModel,
    downloadFile,
    openDownloadDialog,
    refreshPage,
    setFilterResetFunc,
    paginationModel,
    paginationMeta,
    paging,
    onPaginationModelChange,
  } = props;
  console.log(rowCount, "loading child component");

  const { openDialog } = useDialog();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canDelete = user && DELETE_ROLES.includes(user.businessType);

  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const openDeleteDialog = (radUserIds) =>
    openDialog(
      "delete_users",
      radUserIds.length === 1 ? "Delete User" : "Delete Users",
      <DeleteUsersModal
        key={getTimeStamp()}
        radUserIds={radUserIds}
        onDeleted={() => {
          setRowSelectionModel([]);
          refreshPage();
        }}
      />,
      "sm",
    );

  const actionColumn = {
    field: "action",
    headerName: "Action",
    filter: false,
    sortable: false,
    width: 80,
    renderCell: (params) => (
      <EditDelete
        menus={["delete"]}
        rowId={params.id}
        deleteItemClick={() => openDeleteDialog([params.id])}
      />
    ),
  };

  const columns = [
    ...(canDelete ? [actionColumn] : []),
    {
      field: "userName",
      headerName: "User Name",
      renderCell: (params) => <Link to={"/user/details?id=" + params.id}>{params.value}</Link>,
      width: 300,
    },
    {
      field: "connectionStatus",
      headerName: "Connection Status",
      renderCell: (params) => {
        return userStatus(params.row["connectionStatus"]);
      },
      width: 200,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      width: 200,
    },
    {
      field: "emailId",
      headerName: "Email Id",
      width: 300,
    },
    {
      field: "mobileNo",
      headerName: "Mobile No",
      width: 150,
    },
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: 130,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
      />,
    );
  };

  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <Toolbar
            addPage={addPage}
            downloadFile={downloadFile}
            openDownloadDialog={openDownloadDialog}
            refreshPage={refreshPage}
            openFilterDialog={openFilterDialog}
            isLoadingDownload={isLoadingDownload}
            downloadFileType={downloadFileType}
          />
        }
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            {trans("suspended_users")}
          </Typography>
        }
        subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
      />
      <CardContent sx={{ height: height - 180, display: "flex", flexDirection: "column" }}>
        {canDelete && (
          <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={rowSelectionModel.length === 0}
              onClick={() => openDeleteDialog(rowSelectionModel)}
            >
              Delete Selected ({rowSelectionModel.length})
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => navigate("/user/delete-history")}
            >
              Deleted Users History
            </Button>
          </Box>
        )}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DataGrid
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            rowCount={rowCount}
            getRowId={(row) => row[id]}
            columns={columns}
            rows={rows}
            page={paging.pageNumber}
            pageSize={paging.pageSize}
            paginationMeta={paginationMeta}
            loading={isLoading}
            pageSizeOptions={[25, 50, 100]}
            paginationMode="server"
            onPaginationModelChange={onPaginationModelChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            paginationModel={paginationModel}
            checkboxSelection={canDelete}
            disableRowSelectionOnClick
            keepNonExistentRowsSelected
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
CustomDataGrid.propTypes = {
  editPage: PropTypes.func.isRequired,
  addPage: PropTypes.func.isRequired,
  trans: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  rowCount: PropTypes.number.isRequired,
  paging: PropTypes.object.isRequired,
  onPageChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  handleSortModelChange: PropTypes.func.isRequired,
  sortModel: PropTypes.object.isRequired,
};
export default CustomDataGrid;
